import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import clsx from 'clsx';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  widthClass?: string;
}

export const Modal: React.FC<Props> = ({ open, onClose, title, children, widthClass = 'max-w-lg' }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-2 sm:py-8"
      onClick={onClose}
    >
      <div
        className={clsx(
          // Mobile: full width, no margins, rounded only at top so the body
          // can scroll cleanly. Tablet+: centred card with margin.
          'relative bg-surface border border-border rounded-t-2xl sm:rounded-2xl shadow-elevated w-full sm:mx-4 max-h-[95vh] sm:max-h-[85vh] flex flex-col animate-fade-in',
          widthClass,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — sticky at top so close button is always reachable on long modals */}
        <div className="flex items-center justify-between border-b border-border px-4 sm:px-6 py-3 sm:py-4 shrink-0">
          <h3 className="font-display text-base font-semibold tracking-tight text-text-primary truncate pr-2">{title}</h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {/* Body — scrolls when content overflows on small screens */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};
